import { NextPageContext } from 'next';
import { Router } from 'shared-routing';
import { isTrackingParam } from './trackingParams';

// A fragment, not a query param. It never reaches the server, so it cannot be
// crawled into an indexable url or published in og:url, and it never appears in
// router.query for a listing to spread back out.
export const NOT_FOUND_HASH = 'notFound';

// What the listings themselves read out of the url, per sanitizeJourneyParams in
// utils/yourJourney. An allow-list rather than a deny-list of route segments:
// anything else a caller appends would otherwise ride onto an indexable listing
// url, and any of them can be minted at will.
//
// promoRateCode is deliberately not here. The rates listing strips it from its
// own urls, and setJourneyParams has already written it to a cookie by the time
// this redirect is built, so the guest keeps their promo without it being
// published in the address bar, the history entry and the outgoing Referer.
const LISTING_PARAM_KEYS = [
  'fromDate',
  'toDate',
  'arrivalDate',
  'spaDate',
  'adults',
  'children',
  'infants',
  'guests',
  'spaAdults',
  'childAges',
  'rooms',
  'tableAdults',
  'tableChildren',
];

export const buildNotFoundLocation = (listing: string, query: NextPageContext['query'] = {}) => {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (!(LISTING_PARAM_KEYS.includes(key) || isTrackingParam(key)) || value === undefined) return;
    // First value of a repeat: sanitizeJourneyParams discards a param it reads as
    // an array, so passing the duplicate on would lose the value altogether.
    params.set(key, Array.isArray(value) ? value[0] : String(value));
  });

  const search = params.toString();

  return `${listing}${search ? `?${search}` : ''}#${NOT_FOUND_HASH}`;
};

export const redirectToListing = (
  res: NextPageContext['res'],
  listing: string,
  query: NextPageContext['query'] = {}
) => {
  const location = buildNotFoundLocation(listing, query);

  if (res) {
    // A page can reach here after an earlier guard has already redirected and
    // ended the response. Writing again throws ERR_HTTP_HEADERS_SENT, which
    // _app turns into a 500.
    if (res.headersSent || res.writableEnded) return;

    res.writeHead(302, { Location: location });
    res.end();
    return;
  }

  // replace, not push — the dead URL must not come back on the back button.
  // The detail page is unmounting, so this navigation can be cancelled.
  Router.replace(location).catch(() => {});
};
